import { useState } from 'react';
import { Card } from '../../components/Card/Card';
import { CategoryRow } from './CategoryRow';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useUiStore } from '../../store/ui';
import {
  useCreateCategory,
  useCreateItem,
  useDeleteCategory,
  useDeleteItem,
  useUpdateCategory,
  useUpdateItem,
  useYearBundle,
} from '../../store/queryHooks';
import { kr, signKr } from '../../lib/format';
import { MONTH_NAMES } from '../../lib/dates';
import type { Category, CategoryKind } from '@budgetx/shared';
import styles from './MaanedsbudgetPage.module.css';

type ConfirmTarget =
  | { kind: 'item'; id: number; name: string }
  | { kind: 'category'; id: number; name: string; itemCount: number };

const NEW_CATEGORY_LABEL: Record<CategoryKind, string> = {
  income: 'Ny indtægtskategori',
  fast: 'Ny udgiftskategori',
  variabel: 'Ny kategori',
  opsparing: 'Ny opsparingskategori',
};

export function MaanedsbudgetPage() {
  const { year, month, openCats, toggleCat, openCat, setToast } = useUiStore();
  const bundle = useYearBundle(year);
  const updateItem = useUpdateItem(year);
  const updateCategory = useUpdateCategory(year);
  const createCategory = useCreateCategory(year);
  const createItem = useCreateItem(year);
  const deleteItem = useDeleteItem(year);
  const deleteCategory = useDeleteCategory(year);
  const [confirmDelete, setConfirmDelete] = useState<ConfirmTarget | null>(null);

  if (bundle.isPending) return <div>Indlæser…</div>;
  if (bundle.isError || !bundle.data) return <div>Kunne ikke hente året.</div>;

  const cats = bundle.data.categories.filter((c) => c.kind !== 'income');

  const totalBudget = cats.reduce(
    (s, c) => s + c.items.reduce((ss, it) => ss + (it.budgets[month] ?? 0), 0),
    0,
  );
  const totalActual = cats.reduce(
    (s, c) => s + c.items.reduce((ss, it) => ss + (it.actuals[month] ?? 0), 0),
    0,
  );
  const totalDiff = totalBudget - totalActual;
  const totalDiffClass = totalDiff < 0 ? styles.diffOver : styles.diffOk;

  async function patchItemName(itemId: number, current: string, next: string) {
    if (current === next) return;
    try {
      await updateItem.mutateAsync({ id: itemId, name: next });
    } catch {
      setToast('Kunne ikke gemme navn');
    }
  }

  async function patchCategoryName(id: number, current: string, next: string) {
    if (current === next) return;
    try {
      await updateCategory.mutateAsync({ id, name: next });
    } catch {
      setToast('Kunne ikke gemme kategorinavn');
    }
  }

  async function patchItemBudget(item: { id: number; budgets: number[] }, next: number) {
    const budgets = item.budgets.slice();
    budgets[month] = next;
    try {
      await updateItem.mutateAsync({ id: item.id, budgets: budgets as number[] });
    } catch {
      setToast('Kunne ikke gemme budget');
    }
  }

  async function patchItemActual(
    item: { id: number; actuals: (number | null)[] },
    next: number | null,
  ) {
    const actuals = item.actuals.slice();
    actuals[month] = next;
    try {
      await updateItem.mutateAsync({ id: item.id, actuals: actuals as (number | null)[] });
    } catch {
      setToast('Kunne ikke gemme faktisk');
    }
  }

  async function addItem(categoryId: number) {
    try {
      await createItem.mutateAsync({ categoryId, name: 'Ny postering' });
      setToast(`Ny postering tilføjet til alle måneder i ${year}`);
    } catch {
      setToast('Kunne ikke tilføje postering');
    }
  }

  async function addCategory(kind: CategoryKind) {
    const name = NEW_CATEGORY_LABEL[kind];
    try {
      const created = await createCategory.mutateAsync({ name, kind });
      if (!created) throw new Error('missing category response');
      await createItem.mutateAsync({ categoryId: created.id, name: 'Ny postering' });
      openCat(created.name);
      setToast('Ny kategori tilføjet');
    } catch {
      setToast('Kunne ikke tilføje kategori');
    }
  }

  function requestDeleteItem(id: number, name: string) {
    setConfirmDelete({ kind: 'item', id, name });
  }

  function requestDeleteCategory(c: Category) {
    setConfirmDelete({ kind: 'category', id: c.id, name: c.name, itemCount: c.items.length });
  }

  async function performDelete() {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === 'item') {
        await deleteItem.mutateAsync(confirmDelete.id);
      } else {
        await deleteCategory.mutateAsync(confirmDelete.id);
      }
    } catch {
      setToast('Kunne ikke slette');
    } finally {
      setConfirmDelete(null);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.kpiRow}>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Budgetteret</div>
          <div className={styles.kpiValue}>{kr(totalBudget)}</div>
        </Card>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Faktisk</div>
          <div className={styles.kpiValue}>{kr(totalActual)}</div>
        </Card>
        <Card compact className={styles.kpi}>
          <div className={styles.kpiLabel}>Difference</div>
          <div className={`${styles.kpiValue} ${totalDiffClass}`}>{signKr(totalDiff)}</div>
        </Card>
      </div>

      <Card className={styles.tableCard}>
        <div className={styles.tableHead}>
          <h2 className={styles.tableTitle}>
            Kategorier · {MONTH_NAMES[month]} {year}
          </h2>
          <div className={styles.hint}>Klik en kategori for at se posteringer</div>
        </div>
        <div className={styles.grid}>
          <div>Kategori</div>
          <div className={styles.gridRight}>Budget</div>
          <div className={styles.gridRight}>Faktisk</div>
          <div className={styles.gridRight}>Difference</div>
          <div />
          <div />
        </div>
        {cats.map((c) => {
          const isOpen = openCats.includes(c.name);
          return (
            <CategoryRow
              key={c.id}
              category={c}
              month={month}
              isOpen={isOpen}
              onToggle={() => toggleCat(c.name)}
              onRenameCategory={(name) => patchCategoryName(c.id, c.name, name)}
              onRenameItem={(id, name) => {
                const it = c.items.find((x) => x.id === id);
                if (it) patchItemName(id, it.name, name);
              }}
              onBudgetChange={(id, v) => {
                const it = c.items.find((x) => x.id === id);
                if (it) patchItemBudget(it, v);
              }}
              onActualChange={(id, v) => {
                const it = c.items.find((x) => x.id === id);
                if (it) patchItemActual(it, v);
              }}
              onDeleteItem={(id) => {
                const it = c.items.find((x) => x.id === id);
                if (it) requestDeleteItem(id, it.name);
              }}
              onDeleteCategory={() => requestDeleteCategory(c)}
              onAddItem={() => addItem(c.id)}
            />
          );
        })}
        <div className={styles.totals}>
          <div className={styles.totalsLabel}>I alt</div>
          <div className={`${styles.totalsNum}`} style={{ color: 'var(--muted)' }}>
            {kr(totalBudget)}
          </div>
          <div className={styles.totalsNum}>{kr(totalActual)}</div>
          <div className={`${styles.totalsNum} ${totalDiffClass}`}>{signKr(totalDiff)}</div>
          <div />
          <div />
        </div>
        <div className={styles.addCategoryRow}>
          <span className={styles.addCategoryLabel}>Tilføj kategori:</span>
          <button className={styles.addCategoryBtn} onClick={() => addCategory('income')}>
            + Indtægt
          </button>
          <button className={styles.addCategoryBtn} onClick={() => addCategory('fast')}>
            + Fast udgift
          </button>
          <button className={styles.addCategoryBtn} onClick={() => addCategory('variabel')}>
            + Variabel udgift
          </button>
        </div>
      </Card>

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.kind === 'item' ? 'Slet postering' : 'Slet kategori'}
          message={
            confirmDelete.kind === 'item'
              ? `Slet posteringen "${confirmDelete.name}"?`
              : confirmDelete.itemCount > 0
                ? `Slet kategorien "${confirmDelete.name}"? Dette fjerner også ${confirmDelete.itemCount} ${confirmDelete.itemCount === 1 ? 'postering' : 'posteringer'}.`
                : `Slet kategorien "${confirmDelete.name}"?`
          }
          danger
          onConfirm={performDelete}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}
